/* eslint-disable camelcase */
import { IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { useCallback } from 'react';
import { ApiError } from 'shared/api';
import { APP_ROUTES } from 'shared/constants';
import { useAppDispatch, useAppSelector } from 'shared/hooks';

import { AUTH_SCOPES, getAuthorityByPolicyName } from '../authConfig';
import { getUserAuthInfo } from '../store/authSlice';
import { UserInfo } from '../types';

export default function useLogin(
    instance: IPublicClientApplication
) {
    const accessTokenConfig = useAppSelector(state => state.auth.accessToken);
    const dispatch = useAppDispatch();

    return useCallback(async (redirectStartPage?: string) => {
        if (accessTokenConfig.fullAccess) {
            return;
        }

        let config = buildDefaultConfig(redirectStartPage);

        if (accessTokenConfig.token) {
            const userInfoResponse = await dispatch(getUserAuthInfo());

            if (userInfoResponse instanceof ApiError) {
                return;
            }

            const userInfo = (userInfoResponse as unknown as ReturnType<typeof getUserAuthInfo.fulfilled>).payload;

            config = buildConfigForUser(userInfo, redirectStartPage);
        }

        await instance.loginRedirect(config);
    }, [ accessTokenConfig.fullAccess, accessTokenConfig.token, dispatch, instance ]);
}

function buildConfigForUser(userInfo: UserInfo, redirectStartPage?: string): RedirectRequest {
    return {
        ...buildDefaultConfig(redirectStartPage),
        authority: getAuthorityByPolicyName(userInfo.b2CPolicy),
        extraQueryParameters: {
            id_token_hint: userInfo.idTokenHint,
            login_hint: userInfo.email,
            ...(userInfo.locale ? { ui_locales: userInfo.locale } : {}),
            prompt: 'login'
        }
    };
}

function buildDefaultConfig(redirectStartPage?: string): RedirectRequest {
    return {
        scopes: AUTH_SCOPES,
        ...(redirectStartPage
            ? { redirectStartPage: `${ window.location.origin }${ APP_ROUTES.basename }${ redirectStartPage }` }
            : {})
    };
}
